import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { FaComment } from 'react-icons/fa';
import './index.css';

if (typeof document !== 'undefined' && document.querySelector('#root')) {
  Modal.setAppElement('#root');
}

const ProjectAdmission = () => {
  const [walkins, setWalkins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWalkin, setSelectedWalkin] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCounselors, setSelectedCounselors] = useState([]);
  const [showCounselorDropdown, setShowCounselorDropdown] = useState(false);
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [showCenterDropdown, setShowCenterDropdown] = useState(false);
  const [projectTypeFilter, setProjectTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromFollowUpDate, setFromFollowUpDate] = useState('');
  const [toFollowUpDate, setToFollowUpDate] = useState('');
  const [guides, setGuides] = useState([]);
  const counselorDropdownRef = useRef(null);
  const centerDropdownRef = useRef(null);
  const [projectId, setProjectId] = useState('');
  const [selectedGuide, setSelectedGuide] = useState('');
  const [lastProjectId, setLastProjectId] = useState(null);
  const [mostRecentProjectId, setMostRecentProjectId] = useState(null);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [selectedWalkinForRemarks, setSelectedWalkinForRemarks] = useState(null);
  const [remarkForm, setRemarkForm] = useState({
    notes: '',
    nextFollowUpDate: ''
  });
  const api = process.env.REACT_APP_API || 'http://localhost:5000';
  const firstInputRef = useRef(null);

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm();

  // Helper: get college code from name
  const getCollegeCodeFromName = (name) => name?.split(' ')[0]?.toUpperCase() || 'COLL';

  // Helper: map guide name to initials
  const getGuideInitial = (guideName) => {
    if (!guideName) return 'XX';
    const map = {
      'vijay': 'VJ', 'deepak': 'D', 'shravana': 'SH', 'srikanth': 'SK',
      'dinesh kumar': 'DK', 'aarthi': 'AR', 'suvarna': 'SV',
      'durgaprasad': 'DP', 'rajesh': 'R', 'nagajyothi': 'NY','nikitha':'NK'
    };
    const key = guideName.toLowerCase().trim();
    return map[key] || guideName.split(' ').map(w => w[0]).join('').toUpperCase();
  };

  // Fetch all walkins
  const fetchWalkins = async () => {
    try {
      const token = localStorage.getItem("jwt_token");
      
      if (!token) {
        setError('Please login to view walk-ins');
        setLoading(false);
        return;
      }

      const res = await fetch(`${api}/walkins`, {
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        }
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ 
          error: 'Failed to fetch walkins',
          message: `Server responded with status ${res.status}` 
        }));
        
        if (res.status === 401 || res.status === 403) {
          // Token expired or invalid - clear token and show message
          localStorage.removeItem("jwt_token");
          setError(errorData.message || 'Your session has expired. Please login again.');
        } else {
          setError(errorData.message || errorData.error || 'Failed to fetch walkins');
        }
        setLoading(false);
        return;
      }

      const data = await res.json();
      setWalkins(data);
      const uniqueGuides = [...new Set(data.map(w => w.councillorName))].filter(Boolean);
      setGuides(uniqueGuides);
      setError(null);
    } catch (err) {
      console.error('Error fetching walkins:', err);
      setError(err.message || 'Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWalkins(); }, [api]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (counselorDropdownRef.current && !counselorDropdownRef.current.contains(event.target)) {
        setShowCounselorDropdown(false);
      }
      if (centerDropdownRef.current && !centerDropdownRef.current.contains(event.target)) {
        setShowCenterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch last project ID (fallback)
  useEffect(() => {
    const fetchLastProjectId = async () => {
      try {
        const res = await fetch(`${api}/project-admissions/last-id`);
        if (!res.ok) throw new Error('Failed to fetch last project ID');
        const data = await res.json();
        setLastProjectId(data.lastProjectId);
      } catch (err) { console.error(err); }
    };
    fetchLastProjectId();
  }, [api]);

  // Fetch most recent project ID for given college + branch + guide
  const fetchMostRecentProjectId = async (collegeCode, branchCode, guideInitial, guideName) => {
    try {
      const url = `${api}/project-admissions/most-recent?collegeCode=${collegeCode}&branchCode=${branchCode}&guideInitial=${guideInitial}`;
      const res = await fetch(url);
      const data = await res.json();
      const recentId = data?.mostRecentProjectId || lastProjectId || null;
      setMostRecentProjectId(recentId);

      let nextSerial = 1;
      if (recentId) {
        const match = recentId.match(/(\d{4})$/); // last 4 digits
        if (match && match[1]) nextSerial = parseInt(match[1], 10) + 1;
      }

      const newId = `26${collegeCode}_${branchCode}_${guideInitial}${String(nextSerial).padStart(4, '0')}`;
      setProjectId(newId);
      setValue('projectId', newId);
      setValue('guide1', guideName);
    } catch (error) {
      const fallbackId = `26${collegeCode}_${branchCode}_${guideInitial}0001`;
      setProjectId(fallbackId);
      setValue('projectId', fallbackId);
      setValue('guide1', guideName);
    }
  };

  // Open modal
  const openModal = async (walkin) => {
    setSelectedWalkin(walkin);
    const guide = walkin.councillorName || '';
    setSelectedGuide(guide);
    setIsModalOpen(true);

    // Auto-fill form fields from walkin data
    setValue('projectName', walkin.projectTitle || '');
    setValue('phoneNumber1', walkin.phoneNumber || '');
    setValue('phoneNumber2', walkin.phoneNumber2 || '');
    setValue('councillor', walkin.councillorName || '');
    setValue('studentName1', walkin.studentName || '');
    setValue('studentName2', walkin.studentName2 || '');

    const code = walkin.collegeCode || getCollegeCodeFromName(walkin.collegeName);
    const branch = walkin.branch || 'GEN';
    const initial = getGuideInitial(guide);
    await fetchMostRecentProjectId(code, branch, initial, guide);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedWalkin(null);
    reset();
  };

  const handleGuideChange = async (e) => {
    const guide = e.target.value;
    setSelectedGuide(guide);
    if (!selectedWalkin) return;
    const code = selectedWalkin.collegeCode || getCollegeCodeFromName(selectedWalkin.collegeName);
    const branch = watch('branch') || 'GEN';
    const initial = getGuideInitial(guide);
    await fetchMostRecentProjectId(code, branch, initial, guide);
  };

  // Submit form
  const onSubmit = async (data) => {
    if (!selectedWalkin) return;
    data.walkIn = selectedWalkin._id;
    data.guideInitial = getGuideInitial(data.guide1);
    data.projectId = projectId;

    try {
      const res = await fetch(`${api}/project-admissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("jwt_token")}`
        },
        body: JSON.stringify(data)
      });
      const result = await res.json();
      if (res.ok) {
        alert(result.message || 'Saved');
        fetchWalkins();
        closeModal();
      } else alert(result.message || 'Failed to save');
    } catch (err) {
      console.error(err);
      alert('Error saving project admission');
    }
  };

  // Handle view remarks
  const handleViewRemarks = async (walkin) => {
    try {
      const token = localStorage.getItem("jwt_token");
      // First check if this walkin has been enrolled (has a project admission)
      // We need to find the project admission by walkin ID or student details
      const response = await fetch(`${api}/project-admissions`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const admissions = await response.json();
        // Find admission that matches this walkin
        const matchingAdmission = admissions.find(adm => 
          adm.studentName1 === walkin.studentName && 
          adm.phoneNumber1 === walkin.mobileNumber
        );

        if (matchingAdmission) {
          // Fetch full admission details
          const detailResponse = await fetch(`${api}/project-admissions/${matchingAdmission._id}`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });

          if (detailResponse.ok) {
            const data = await detailResponse.json();
            setSelectedWalkinForRemarks({ ...walkin, admission: data });
            setShowRemarksModal(true);
          } else {
            // If project admission fetch fails, try walkin remarks
            console.warn('Failed to fetch project admission, trying walkin:', detailResponse.status);
            const walkinResponse = await fetch(`${api}/walkins/${walkin._id}`, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            });
            if (walkinResponse.ok) {
              const walkinData = await walkinResponse.json();
              setSelectedWalkinForRemarks({ ...walkinData, isWalkin: true });
              setShowRemarksModal(true);
            } else {
              const errorData = await walkinResponse.json().catch(() => ({ message: 'Failed to fetch remarks' }));
              console.error('Failed to fetch walkin remarks:', errorData);
              alert(errorData.message || 'Failed to load remarks. Please try again.');
            }
          }
        } else {
          // Not enrolled, show walkin remarks
          const walkinResponse = await fetch(`${api}/walkins/${walkin._id}`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          if (walkinResponse.ok) {
            const walkinData = await walkinResponse.json();
            setSelectedWalkinForRemarks({ ...walkinData, isWalkin: true });
            setShowRemarksModal(true);
          } else {
            const errorData = await walkinResponse.json().catch(() => ({ message: 'Failed to fetch walkin remarks' }));
            console.error('Failed to fetch walkin remarks:', errorData);
            alert(errorData.message || 'Failed to load remarks. Please try again.');
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch admissions list' }));
        console.error('Failed to fetch admissions:', errorData);
        setError(errorData.message || 'Failed to fetch admissions list');
      }
    } catch (error) {
      console.error('Error fetching remarks:', error);
      alert('An error occurred while loading remarks. Please try again.');
      alert('Error fetching remarks');
    }
  };

  // Handle add remark
  const handleAddRemark = async (e) => {
    e.preventDefault();
    if (!remarkForm.notes || !remarkForm.nextFollowUpDate) {
      alert('Please fill all fields');
      return;
    }

    try {
      const token = localStorage.getItem("jwt_token");
      const targetId = selectedWalkinForRemarks?.admission?._id || selectedWalkinForRemarks?._id;
      const endpoint = selectedWalkinForRemarks?.isWalkin 
        ? `${api}/walkins/${targetId}/remarks`
        : `${api}/project-admissions/${targetId}/remarks`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(remarkForm),
      });

      if (response.ok) {
        const data = await response.json();
        if (selectedWalkinForRemarks?.isWalkin) {
          setSelectedWalkinForRemarks({ ...selectedWalkinForRemarks, ...data.walkin });
        } else {
          setSelectedWalkinForRemarks({ 
            ...selectedWalkinForRemarks, 
            admission: data.admission 
          });
        }
        setRemarkForm({ notes: '', nextFollowUpDate: '' });
        alert('Remark added successfully');
        fetchWalkins();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to add remark');
      }
    } catch (error) {
      console.error('Error adding remark:', error);
      alert('Error adding remark');
    }
  };

  const closeRemarksModal = () => {
    setShowRemarksModal(false);
    setSelectedWalkinForRemarks(null);
    setRemarkForm({ notes: '', nextFollowUpDate: '' });
  };

  // Handle counselor selection
  const handleCounselorToggle = (counselor) => {
    setSelectedCounselors(prev => {
      if (prev.includes(counselor)) {
        return prev.filter(c => c !== counselor);
      } else {
        return [...prev, counselor];
      }
    });
  };

  // Clear all selected counselors
  const clearCounselorFilter = () => {
    setSelectedCounselors([]);
  };

  // Handle center selection
  const handleCenterToggle = (center) => {
    setSelectedCenters(prev => {
      if (prev.includes(center)) {
        return prev.filter(c => c !== center);
      } else {
        return [...prev, center];
      }
    });
  };

  // Clear all selected centers
  const clearCenterFilter = () => {
    setSelectedCenters([]);
  };

  // Get unique centers from walkins
  const availableCenters = [...new Set(
    walkins
      .map(w => w.center || w.center1) // Support both 'center' and 'center1' for backward compatibility
      .filter(Boolean)
  )].sort();

  // Filter walkins
  const filteredWalkins = walkins.filter(w => {
    // Status filter
    if (statusFilter && w.status !== statusFilter) return false;
    
    // Counselor filter
    if (selectedCounselors.length > 0 && !selectedCounselors.includes(w.councillorName)) return false;
    
    // Project Type filter
    if (projectTypeFilter && w.projectType !== projectTypeFilter) return false;
    
    // Center filter
    if (selectedCenters.length > 0) {
      const walkinCenter = w.center || w.center1; // Support both 'center' and 'center1' for backward compatibility
      if (!walkinCenter || !selectedCenters.includes(walkinCenter)) return false;
    }
    
    // Created Date filter
    const walkinDate = w.date ? new Date(w.date) : (w.createdAt ? new Date(w.createdAt) : null);
    if (walkinDate) {
      // Set time to start of day for comparison
      const walkinDateOnly = new Date(walkinDate.getFullYear(), walkinDate.getMonth(), walkinDate.getDate());
      
      if (fromDate) {
        const fromDateObj = new Date(fromDate);
        fromDateObj.setHours(0, 0, 0, 0);
        if (walkinDateOnly < fromDateObj) return false;
      }
      
      if (toDate) {
        const toDateObj = new Date(toDate);
        toDateObj.setHours(23, 59, 59, 999);
        if (walkinDateOnly > toDateObj) return false;
      }
    } else {
      // If no date and filters are set, exclude it
      if (fromDate || toDate) return false;
    }
    
    // Follow-up Date filter
    if (fromFollowUpDate || toFollowUpDate) {
      // Get all next follow-up dates from remarks array
      const followUpDates = [];
      if (w.remarks && Array.isArray(w.remarks) && w.remarks.length > 0) {
        w.remarks.forEach(remark => {
          if (remark.nextFollowUpDate) {
            followUpDates.push(new Date(remark.nextFollowUpDate));
          }
        });
      }
      
      // If no follow-up dates exist and filters are set, exclude it
      if (followUpDates.length === 0) return false;
      
      // Check if any follow-up date falls within the range
      let hasMatchingFollowUp = false;
      for (const followUpDate of followUpDates) {
        const followUpDateOnly = new Date(followUpDate.getFullYear(), followUpDate.getMonth(), followUpDate.getDate());
        let matchesFrom = true;
        let matchesTo = true;
        
        if (fromFollowUpDate) {
          const fromFollowUpDateObj = new Date(fromFollowUpDate);
          fromFollowUpDateObj.setHours(0, 0, 0, 0);
          if (followUpDateOnly < fromFollowUpDateObj) matchesFrom = false;
        }
        
        if (toFollowUpDate) {
          const toFollowUpDateObj = new Date(toFollowUpDate);
          toFollowUpDateObj.setHours(23, 59, 59, 999);
          if (followUpDateOnly > toFollowUpDateObj) matchesTo = false;
        }
        
        if (matchesFrom && matchesTo) {
          hasMatchingFollowUp = true;
          break;
        }
      }
      
      if (!hasMatchingFollowUp) return false;
    }
    
    return true;
  });

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="project-admission-container">
      <div className="project-admission-header">
        <h1>Walkin Information</h1>
        <span className="project-admission-count">Total Walkins: {filteredWalkins.length}</span>
      </div>

      {/* Filters */}
      <div className="filters-container">
        <div className="filters-grid">
          <div className="filter-item">
            <label>Status</label>
            <select 
              value={statusFilter} 
              onChange={e => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="joined">Joined</option>
              <option value="not joined">Not Joined</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Project Type</label>
            <select 
              value={projectTypeFilter} 
              onChange={e => setProjectTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Types</option>
              <option value="existing">Already Existing</option>
              <option value="new">New Project</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Counselor</label>
          <div className="counselor-multiselect" ref={counselorDropdownRef}>
            <div 
              className="counselor-select-display"
              onClick={() => setShowCounselorDropdown(!showCounselorDropdown)}
            >
              {selectedCounselors.length === 0 
                ? 'Select Counselors...' 
                : `${selectedCounselors.length} selected`}
              <span className="dropdown-arrow">{showCounselorDropdown ? '▲' : '▼'}</span>
            </div>
            {showCounselorDropdown && (
              <div className="counselor-dropdown">
                <div className="counselor-dropdown-header">
                  <button 
                    type="button"
                    onClick={clearCounselorFilter}
                    className="clear-all-btn"
                  >
                    Clear All
                  </button>
                </div>
                <div className="counselor-options">
                  {guides.map((counselor, i) => (
                    <label key={i} className="counselor-option">
                      <input
                        type="checkbox"
                        checked={selectedCounselors.includes(counselor)}
                        onChange={() => handleCounselorToggle(counselor)}
                      />
                      <span>{counselor}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>

          <div className="filter-item">
            <label>Center</label>
            <div className="counselor-multiselect" ref={centerDropdownRef}>
              <div 
                className="counselor-select-display"
                onClick={() => setShowCenterDropdown(!showCenterDropdown)}
              >
                {selectedCenters.length === 0 
                  ? 'Select Centers...' 
                  : `${selectedCenters.length} selected`}
                <span className="dropdown-arrow">{showCenterDropdown ? '▲' : '▼'}</span>
              </div>
              {showCenterDropdown && (
                <div className="counselor-dropdown">
                  <div className="counselor-dropdown-header">
                    <button 
                      type="button"
                      onClick={clearCenterFilter}
                      className="clear-all-btn"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="counselor-options">
                    {availableCenters.length > 0 ? (
                      availableCenters.map((center, i) => (
                        <label key={i} className="counselor-option">
                          <input
                            type="checkbox"
                            checked={selectedCenters.includes(center)}
                            onChange={() => handleCenterToggle(center)}
                          />
                          <span>{center}</span>
                        </label>
                      ))
                    ) : (
                      <div style={{ padding: '8px 12px', color: '#666' }}>No centers available</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="filter-item">
            <label>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="date-filter-input"
            />
          </div>

          <div className="filter-item">
            <label>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="date-filter-input"
            />
          </div>

          <div className="filter-item">
            <label>Follow-up From Date</label>
            <input
              type="date"
              value={fromFollowUpDate}
              onChange={e => setFromFollowUpDate(e.target.value)}
              className="date-filter-input"
            />
          </div>

          <div className="filter-item">
            <label>Follow-up To Date</label>
            <input
              type="date"
              value={toFollowUpDate}
              onChange={e => setToFollowUpDate(e.target.value)}
              className="date-filter-input"
            />
          </div>

          {(fromDate || toDate || fromFollowUpDate || toFollowUpDate || statusFilter || projectTypeFilter || selectedCounselors.length > 0 || selectedCenters.length > 0) && (
            <div className="filter-item filter-clear-all">
              <button
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                  setFromFollowUpDate('');
                  setToFollowUpDate('');
                  setStatusFilter('');
                  setProjectTypeFilter('');
                  setSelectedCounselors([]);
                  setSelectedCenters([]);
                }}
                className="clear-all-filters-btn"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {filteredWalkins.length === 0 ? (
        <p className="project-admission-no-data">NO DATA</p>
      ) : (
        <div className="table-wrapper">
        <table className="project-admission-table">
          <thead>
            <tr>
              <th>Student Name</th>
              <th>Project Name</th>
              <th>College Name</th>
              <th>Phone Number</th>
              <th>Councillor Name</th>
              <th>Created Date</th>
              <th>Remarks</th>
              <th> Follow-up</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredWalkins.map(walkin => (
              <tr key={walkin._id}>
                <td data-label="Student Name">{walkin.studentName}</td>
                <td data-label="Project Name">{walkin.projectTitle}</td>
                <td data-label="College Name">{walkin.collegeName}</td>
                <td data-label="Phone Number">
                  {walkin.mobileNumber ? (
                    <a
                      href={`https://wa.me/${walkin.mobileNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="phone-link"
                    >
                      {walkin.mobileNumber}
                    </a>
                  ) : '-'}
                </td>
                <td data-label="Councillor Name">{walkin.councillorName}</td>
                <td data-label="Created Date">
                  {walkin.date 
                    ? new Date(walkin.date).toLocaleDateString() 
                    : walkin.createdAt 
                    ? new Date(walkin.createdAt).toLocaleDateString() 
                    : '-'}
                </td>
                <td data-label="Remarks">
                  {(() => {
                    // If status is joined, show "Joined"
                    if (walkin.status === 'joined') {
                      return 'Joined';
                    }
                    
                    // Otherwise show latest remark from remarks array
                    const hasFollowUpRemarks = walkin.remarks && walkin.remarks.length > 0;
                    if (hasFollowUpRemarks) {
                      const latestRemark = walkin.remarks[walkin.remarks.length - 1];
                      const remarkText = latestRemark.notes || 'No remark';
                      return remarkText.length > 50 ? remarkText.substring(0, 50) + '...' : remarkText;
                    }
                    
                    // If no follow-up remarks, check for initial remark
                    const hasInitialRemark = walkin.remark && walkin.remark.trim() !== '';
                    if (hasInitialRemark) {
                      return walkin.remark.length > 50 ? walkin.remark.substring(0, 50) + '...' : walkin.remark;
                    }
                    
                    return 'No remark';
                  })()}
                </td>
                <td data-label="Follow-up">
                  {(() => {
                    // If status is joined, no next follow-up
                    if (walkin.status === 'joined') {
                      return '-';
                    }
                    
                    // Show latest next follow-up date from remarks array
                    const hasFollowUpRemarks = walkin.remarks && walkin.remarks.length > 0;
                    if (hasFollowUpRemarks) {
                      const latestRemark = walkin.remarks[walkin.remarks.length - 1];
                      if (latestRemark.nextFollowUpDate) {
                        return new Date(latestRemark.nextFollowUpDate).toLocaleDateString();
                      }
                    }
                    return '-';
                  })()}
                </td>
                <td data-label="Actions">
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {walkin.status === 'not joined' ? (
                      <button onClick={() => openModal(walkin)}>Enroll</button>
                    ) : <strong>Joined</strong>}
                    <button
                      onClick={() => handleViewRemarks(walkin)}
                      className="remarks-btn"
                      title="View/Add Remarks"
                    >
                      <FaComment /> Remarks
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Modal */}
      {selectedWalkin && (
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          contentLabel="Enroll Modal"
          className="project-admission-modal"
          overlayClassName="project-admission-modal-overlay"
        >
          <h2>Enroll Student</h2>
          {mostRecentProjectId && (
            <div className="most-recent-project-id">
              <strong>Most Recent Project ID:</strong> {mostRecentProjectId}
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="enroll-form">
            {/* Project ID */}
            <div className="form-group">
              <label>Project ID <span className="required">*</span></label>
              <input
                ref={firstInputRef}
                type="text"
                {...register('projectId', { required: true })}
                value={projectId}
                readOnly
              />
              {errors.projectId && <p className="error">Project ID is required</p>}
            </div>

            {/* Project Name */}
            <div className="form-group">
              <label>Project Name <span className="required">*</span></label>
              <input type="text" {...register('projectName', { required: true })} />
              {errors.projectName && <p className="error">Project Name is required</p>}
            </div>

            {/* Project Category */}
            <div className="form-group">
              <label>Project Category <span className="required">*</span></label>
              <select {...register('projectCategory', { required: true })}>
                <option value="">Select Category</option>
                <option value="Blockchain">Blockchain</option>
                <option value="Web Designing">Web Designing</option>
                <option value="Machine Learning">Machine Learning</option>
                <option value="Artificial Intelligence">Artificial Intelligence</option>
                <option value="Deep Learning">Deep Learning</option>
                <option value="Cyber Security">Cyber Security</option>
                <option value="Networking">Networking</option>
                <option value="Cloud Computing">Cloud Computing</option>
                <option value="IoT">IoT</option>
                <option value="Android">Android</option>
              </select>
              {errors.projectCategory && <p className="error">Project Category is required</p>}
            </div>

            {/* Branch Dropdown */}
            <div className="form-group">
              <label>Branch <span className="required">*</span></label>
              <select {...register('branch', { required: true })}>
                <option value="">Select Branch</option>
                <option value="DWK">DWK</option>
                <option value="GWK">GWK</option>
                <option value="HO">HO</option>
                <option value="MVP">MVP</option>
              </select>
              {errors.branch && <p className="error">Branch is required</p>}
            </div>

            {/* Student Names, Phone, Fees, Guides, Deadline, Status, Councillor, Remarks */}
            <div className="form-group">
              <label>Student Name 1 <span className="required">*</span></label>
              <input type="text" {...register('studentName1', { required: true })} />
              {errors.studentName1 && <p className="error">Student Name 1 is required</p>}
            </div>
            <div className="form-group">
              <label>Student Name 2</label>
              <input type="text" {...register('studentName2')} />
            </div>
            <div className="form-group">
              <label>Phone Number 1 <span className="required">*</span></label>
              <input type="text" {...register('phoneNumber1', { required: true, pattern: /^[0-9]{10}$/ })} />
              {errors.phoneNumber1 && (
                <p className="error">
                  {errors.phoneNumber1.type === 'required' ? 'Phone Number 1 is required' : 'Phone Number 1 must be 10 digits'}
                </p>
              )}
            </div>
            <div className="form-group">
              <label>Phone Number 2</label>
              <input type="text" {...register('phoneNumber2', { pattern: /^[0-9]{10}$/ })} />
              {errors.phoneNumber2 && <p className="error">Phone Number 2 must be 10 digits</p>}
            </div>
            <div className="form-group">
              <label>Total Fees <span className="required">*</span></label>
              <input type="number" {...register('totalFees', { required: true })} />
              {errors.totalFees && <p className="error">Total Fees is required</p>}
            </div>
            <div className="form-group">
              <label>Fees Paid <span className="required">*</span></label>
              <input type="number" {...register('feesPaid', { required: true })} />
              {errors.feesPaid && <p className="error">Fees Paid is required</p>}
            </div>
            <div className="form-group">
              <label>Guide 1 <span className="required">*</span></label>
              <select {...register('guide1', { required: true })} value={selectedGuide} onChange={handleGuideChange}>
                <option value="">Select Guide</option>
                <option value="Vijay">Vijay</option>
                <option value="Deepak">Deepak</option>
                <option value="Srikanth">Srikanth</option>
                <option value="Suvarna">Suvarna</option>
                <option value="Dinesh Kumar">Dinesh Kumar</option>
                <option value="Nagajyothi">Nagajyothi</option>
                <option value="Shravana">Shravana</option>
                <option value="Aarthi">Aarthi</option>
                <option value="DurgaPrasad">DurgaPrasad</option>
                <option value="Rajesh">Rajesh</option>
              </select>
              {errors.guide1 && <p className="error">Guide 1 is required</p>}
            </div>
            <div className="form-group">
              <label>Guide 2</label>
              <input type="text" {...register('guide2')} />
            </div>
            <div className="form-group">
              <label>Deadline <span className="required">*</span></label>
              <input type="date" {...register('deadline', { required: true })} />
              {errors.deadline && <p className="error">Deadline is required</p>}
            </div>
            <div className="form-group">
              <label>Status <span className="required">*</span></label>
              <select {...register('status', { required: true })}>
                <option value="">Select Status</option>
                <option value="Pending">Pending</option>
                <option value="Ongoing">Ongoing</option>
                <option value="Completed">Completed</option>
              </select>
              {errors.status && <p className="error">Status is required</p>}
            </div>
            <div className="form-group">
              <label>Councillor <span className="required">*</span></label>
              <input type="text" {...register('councillor', { required: true })} />
              {errors.councillor && <p className="error">Councillor is required</p>}
            </div>
            <div className="form-group">
              <label>Remark</label>
              <textarea {...register('remark')} rows={3} placeholder="Enter initial remarks or notes..."></textarea>
            </div>

            <button type="submit" className="project-admission-save-button">Save</button>
          </form>
          <button onClick={closeModal} className="close-modal-btn">Close</button>
        </Modal>
      )}

      {/* Remarks Modal */}
      {showRemarksModal && selectedWalkinForRemarks && (
        <Modal
          isOpen={showRemarksModal}
          onRequestClose={closeRemarksModal}
          contentLabel="Remarks Modal"
          className="project-admission-modal"
          overlayClassName="project-admission-modal-overlay"
        >
          <div className="remarks-modal-content">
            <div className="remarks-modal-header">
              <h2>Remarks for {selectedWalkinForRemarks.studentName}</h2>
              <button onClick={closeRemarksModal} className="remarks-close-btn">×</button>
            </div>

            {selectedWalkinForRemarks.admission?.remark && (
              <div className="initial-remark">
                <h3>Initial Remark</h3>
                <p className="initial-remark-text">{selectedWalkinForRemarks.admission.remark}</p>
              </div>
            )}

            {selectedWalkinForRemarks.isWalkin && selectedWalkinForRemarks.remark && (
              <div className="initial-remark">
                <h3>Initial Remark</h3>
                <p className="initial-remark-text">{selectedWalkinForRemarks.remark}</p>
              </div>
            )}

            <div className="remarks-form">
              <h3>Add New Remark</h3>
              <form onSubmit={handleAddRemark}>
                <div className="form-group">
                  <label>Notes <span className="required">*</span></label>
                  <textarea
                    value={remarkForm.notes}
                    onChange={(e) => setRemarkForm({ ...remarkForm, notes: e.target.value })}
                    rows="3"
                    required
                    placeholder="Enter follow-up notes..."
                  />
                </div>
                <div className="form-group">
                  <label>Next Follow-up Date <span className="required">*</span></label>
                  <input
                    type="date"
                    value={remarkForm.nextFollowUpDate}
                    onChange={(e) => setRemarkForm({ ...remarkForm, nextFollowUpDate: e.target.value })}
                    required
                  />
                </div>
                <button type="submit" className="remarks-submit-btn">Add Remark</button>
              </form>
            </div>

            <div className="remarks-history">
              <h3>Remarks History</h3>
              {(() => {
                const remarks = selectedWalkinForRemarks.admission?.remarks || selectedWalkinForRemarks.remarks || [];
                return remarks.length > 0 ? (
                  <div className="remarks-timeline">
                    {remarks
                      .slice()
                      .sort((a, b) => new Date(b.followUpDate) - new Date(a.followUpDate))
                      .map((remark, index) => (
                        <div key={index} className="remark-item">
                          <div className="remark-header">
                            <span className="remark-date">
                              {new Date(remark.followUpDate).toLocaleDateString()}
                            </span>
                            <span className={`remark-status ${remark.remainderStatus}`}>
                              {remark.remainderStatus}
                            </span>
                          </div>
                          <div className="remark-notes">{remark.notes}</div>
                          <div className="remark-next-date">
                            <strong>Next Follow-up:</strong>{' '}
                            {new Date(remark.nextFollowUpDate).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="remarks-no-data">No remarks yet. Add your first remark above.</p>
                );
              })()}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProjectAdmission;
